import * as fabric from 'fabric';
import 'mediaelement';
import 'mediaelement/build/mediaelementplayer.min.css';
import { FabricElement } from '../models';
import {
	createDOMElement,
	getCanvasElementPosition,
	registerFabricClass,
	resolveFromObject,
	toObject,
	wrapDOMElement,
} from '../utils';

export interface VideoObject extends FabricElement {
	setSource: (source: string | File) => void;
	setFile: (file: File) => void;
	setSrc: (src: string) => void;
	file?: File;
	src?: string;
	videoElement?: HTMLVideoElement;
	player?: any;
}

class Video extends fabric.Rect {
	static type = 'video';
	superType = 'element';
	hasRotatingPoint = false;
	declare element: HTMLDivElement;
	declare container: string;
	declare videoElement: HTMLVideoElement;
	declare player: any;
	declare src: string | null;
	declare file: File | null;

	constructor(source: string | File, options: any = {}) {
		const { type: _type, ...elementOptions } = options;
		super(elementOptions);
		if (source instanceof File) {
			this.set({ file: source, src: null });
		} else {
			this.set({ file: null, src: source });
		}
		this.set({
			fill: 'rgba(255, 255, 255, 0)',
			stroke: 'rgba(255, 255, 255, 0)',
		});
	}

	setSource(source: any) {
		if (source instanceof File) {
			this.setFile(source);
		} else {
			this.setSrc(source);
		}
	}

	setFile(file: File) {
		this.set({ file, src: null });
		const reader = new FileReader();
		reader.onload = () => {
			this.player.setSrc(reader.result);
		};
		reader.readAsDataURL(file);
	}

	setSrc(src: string) {
		this.set({ file: null, src });
		this.player.setSrc(src);
	}

	toObject(propertiesToInclude: any[] = []) {
		return toObject(super.toObject(propertiesToInclude), this, propertiesToInclude, {
			src: this.get('src'),
			file: this.get('file'),
			container: this.get('container'),
			editable: this.get('editable'),
		});
	}

	_render(ctx: CanvasRenderingContext2D) {
		super._render(ctx);
		if (!this.element) {
			const id = this.get('id') as string;
			const editable = this.get('editable') as boolean;
			const autoplay = this.get('autoplay') as boolean;
			const muted = this.get('muted') as boolean;
			const loop = this.get('loop') as boolean;
			const { scaleX, scaleY, width, height, angle, src, file } = this;
			const zoom = this.canvas.getZoom();
			const { left, top } = getCanvasElementPosition(this, this.canvas);
			this.videoElement = createDOMElement('video', {
				id,
				autoplay: editable ? false : autoplay,
				muted: editable ? false : muted,
				loop: editable ? false : loop,
				preload: 'none',
				controls: false,
			});
			this.element = wrapDOMElement(this.videoElement, 'div', {
				id: `${id}_container`,
				style: `transform: rotate(${angle}deg) scale(${scaleX * zoom}, ${scaleY * zoom});
                        width: ${width}px;
                        height: ${height}px;
						left: ${left}px;
						top: ${top}px;
                        position: absolute;
                        user-select: ${editable ? 'none' : 'auto'};
                        pointer-events: ${editable ? 'none' : 'auto'};`,
			}) as HTMLDivElement;
			document.getElementById(this.container)?.appendChild(this.element);
			this.player = new MediaElementPlayer(id, {
				pauseOtherPlayers: false,
				videoWidth: '100%',
				videoHeight: '100%',
				success: (_mediaeElement: any, _originalNode: any, instance: any) => {
					if (editable) {
						instance.pause();
					}
				},
			});
			this.player.setPlayerSize(width, height);
			if (src) {
				this.setSrc(src);
			} else if (file) {
				this.setFile(file);
			}
		}
	}

	static fromObject(options: any, callback?: any) {
		return resolveFromObject(new Video(options.src || options.file, options), callback);
	}
}

registerFabricClass('Video', Video);

export default Video;
